// ==================================
// File: src/components/SidebarMenu.jsx
// ==================================

import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Collapse,
  Badge,
} from "@mui/material";
import { useState } from "react";

// ===== EXPAND ICONS (FIXED) =====
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";

// ===== CORE =====
import DashboardIcon from "@mui/icons-material/SpaceDashboard";

// ===== ACADEMIC / EXAMS =====
import SchoolIcon from "@mui/icons-material/School";
import QuizIcon from "@mui/icons-material/Quiz";
import AssignmentIcon from "@mui/icons-material/Assignment";
import HistoryIcon from "@mui/icons-material/History";

// ===== PERFORMANCE / ANALYTICS =====
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import BarChartIcon from "@mui/icons-material/BarChart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

// ===== USERS / AGENTS =====
import PeopleIcon from "@mui/icons-material/People";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

// ===== FINANCE =====
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PaymentsIcon from "@mui/icons-material/Payments";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";

// ===== SYSTEM =====
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";

/* =====================================================
   ICON MAP — SINGLE SOURCE OF TRUTH (FINAL)
===================================================== */
const iconMap = {
  // Core
  dashboard: <DashboardIcon fontSize="small" />,

  // Academic / Exams
  subjects: <SchoolIcon fontSize="small" />,        // Subjects / English
  practice: <QuizIcon fontSize="small" />,          // Upload / Practice
  exam: <AssignmentIcon fontSize="small" />,        // Live / JAMB / Post-UTME
  history: <HistoryIcon fontSize="small" />,        // Sessions / History

  // Performance
  leaderboard: <EmojiEventsIcon fontSize="small" />, // Rankings only
  performance: <TrendingUpIcon fontSize="small" />,
  results: <BarChartIcon fontSize="small" />,

  // Users / Agents
  users: <PeopleIcon fontSize="small" />,
  agents: <SupportAgentIcon fontSize="small" />,

  // Finance
  payment: <CreditCardIcon fontSize="small" />,     // Trials / Subscriptions
  payouts: <PaymentsIcon fontSize="small" />,       // Agent payouts
  wallet: <AccountBalanceWalletIcon fontSize="small" />,

  // System
  mail: <MailOutlineIcon fontSize="small" />,
  settings: <SettingsIcon fontSize="small" />,
  logout: <LogoutIcon fontSize="small" />,
};

export default function SidebarMenu({ items, activePath, onNavigate }) {
  const [openMenus, setOpenMenus] = useState({});

  const toggleOpen = (label) => {
    setOpenMenus((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const isActive = (to = "") => activePath.startsWith(to);

  return (
    <List sx={{ width: "100%" }}>
      {items.map((item) => {
        const hasChildren = Array.isArray(item.children);
        const isOpen = openMenus[item.label] || false;
        const selected = isActive(item.to);

        const badgeCount =
          typeof item.badge === "number" && item.badge > 0
            ? item.badge
            : null;

        // ✅ SAFE ICON FALLBACK
        const IconElement =
          iconMap[item.icon] || <DashboardIcon fontSize="small" />;

        return (
          <div key={item.label}>
            <Tooltip title={item.label} placement="right" arrow>
              <ListItemButton
                selected={selected}
                onClick={() =>
                  hasChildren ? toggleOpen(item.label) : onNavigate(item.to)
                }
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {badgeCount ? (
                    <Badge color="error" badgeContent={badgeCount}>
                      {IconElement}
                    </Badge>
                  ) : (
                    IconElement
                  )}
                </ListItemIcon>

                <ListItemText primary={item.label} />

                {hasChildren &&
                  (isOpen ? <ExpandLess /> : <ExpandMore />)}
              </ListItemButton>
            </Tooltip>

            {hasChildren && (
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 4 }}>
                  {item.children.map((child) => (
                    <ListItemButton
                      key={child.label}
                      selected={isActive(child.to)}
                      onClick={() => onNavigate(child.to)}
                      sx={{ borderRadius: 2, mb: 0.5 }}
                    >
                      <ListItemText primary={child.label} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
          </div>
        );
      })}
    </List>
  );
}
